import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import {
    Box,
    Button,
    Checkbox,
    Grid,
    IconButton,
    MenuItem,
    Modal,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { StyledTableCell, StyledTableRow, styles } from '../../style/style';
import axios from '../api/axiosConfig';
import * as React from 'react';
import { useEffect } from 'react';

const newsTableGray = '#5a5a5a';
const newsTableFont = {
    fontFamily: 'Arial, Helvetica, sans-serif',
};
const newsTableHeaderCell = {
    ...newsTableFont,
    fontSize: '14px',
    fontWeight: 600,
    color: newsTableGray,
};
const newsTableBodyCell = {
    ...newsTableFont,
    fontSize: '14px',
    fontWeight: 400,
    color: newsTableGray,
    paddingTop: '14px',
    paddingBottom: '14px',
    paddingLeft: '16px',
    paddingRight: '16px',
};

const emptyForm = { title: '', message: '', startDate: '', endDate: '', createdBy: '' };

/** Only digits; formats as MM/DD/YYYY as user types (max 8 digits). */
function formatDateMmDdYyyy(value) {
    const digits = (value ?? '').replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/** True if value is MM/DD/YYYY with valid month (01–12), valid day for that month, and year before 2999. */
function isValidMmDdYyyy(value) {
    if (!value || typeof value !== 'string' || value.length < 10) return false;
    if (value[2] !== '/' || value[5] !== '/') return false;
    const month = parseInt(value.slice(0, 2), 10);
    const day = parseInt(value.slice(3, 5), 10);
    const year = parseInt(value.slice(6, 10), 10);
    if (year >= 2999) return false;
    if (month < 1 || month > 12 || day < 1 || day > 31) return false;
    const d = new Date(year, month - 1, day);
    return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;
}

/** Returns Date from MM/DD/YYYY or null if invalid. */
function parseMmDdYyyyToDate(value) {
    if (!isValidMmDdYyyy(value)) return null;
    const month = parseInt(value.slice(0, 2), 10);
    const day = parseInt(value.slice(3, 5), 10);
    const year = parseInt(value.slice(6, 10), 10);
    return new Date(year, month - 1, day);
}

/** Parse MM/DD/YYYY or YYYY-MM-DD to Date (for comparing with existing row dates). Returns null if unparseable. */
function parseAnyDateToDate(value) {
    if (!value || typeof value !== 'string' || value.length < 10) return null;
    let month, day, year;
    if (value[2] === '/' && value[5] === '/') {
        month = parseInt(value.slice(0, 2), 10);
        day = parseInt(value.slice(3, 5), 10);
        year = parseInt(value.slice(6, 10), 10);
    } else if (value[4] === '-' && value[7] === '-') {
        year = parseInt(value.slice(0, 4), 10);
        month = parseInt(value.slice(5, 7), 10);
        day = parseInt(value.slice(8, 10), 10);
    } else {
        return null;
    }
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const d = new Date(year, month - 1, day);
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
    return d;
}

/** True if [startA, endA] and [startB, endB] overlap (inclusive). */
function dateRangesOverlap(startA, endA, startB, endB) {
    const a = parseAnyDateToDate(startA);
    const b = parseAnyDateToDate(endA);
    const c = parseAnyDateToDate(startB);
    const d = parseAnyDateToDate(endB);
    if (!a || !b || !c || !d) return false;
    return a.getTime() <= d.getTime() && b.getTime() >= c.getTime();
}

export default function AdminNewsTable() {
    const ApiList = useSelector((state) => state.apiList.apiList);
    const [newsRows, setNewsRows] = React.useState([]);
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [searchInput, setSearchInput] = React.useState('');
    const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
    const [addModalOpen, setAddModalOpen] = React.useState(false);
    const [editModalOpen, setEditModalOpen] = React.useState(false);
    const [editRow, setEditRow] = React.useState(null);
    const [formValues, setFormValues] = React.useState(emptyForm);
    const [addError, setAddError] = React.useState('');
    const [editError, setEditError] = React.useState('');
    const [deleteError, setDeleteError] = React.useState('');
    const [addValidationAttempted, setAddValidationAttempted] = React.useState(false);
    const [editValidationAttempted, setEditValidationAttempted] = React.useState(false);
    const [sortColumn, setSortColumn] = React.useState('endDate');
    const [sortDirection, setSortDirection] = React.useState('desc');

    const fetchNewsMessages = React.useCallback(() => {
        const url = ApiList.getnewsmessages;
        axios.get(url, {
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        }).then((response) => {
            const list = response.data?.newsMessages;
            setNewsRows(Array.isArray(list) ? list : []);
        }).catch(() => {
            setNewsRows([]);
        });
    }, [ApiList.getnewsmessages]);

    useEffect(() => {
        fetchNewsMessages();
    }, [fetchNewsMessages]);

    const searchLower = searchInput.trim().toLowerCase();
    const filteredRows = searchLower
        ? newsRows.filter(
            (row) =>
                (row.title && row.title.toLowerCase().includes(searchLower)) ||
                (row.message && row.message.toLowerCase().includes(searchLower))
        )
        : newsRows;

    const dateToSortValue = (value) => {
        const d = parseAnyDateToDate(value);
        return d ? d.getTime() : 0;
    };
    const sortedRows = React.useMemo(() => {
        const dir = sortDirection === 'asc' ? 1 : -1;
        return [...filteredRows].sort((a, b) => {
            let cmp = 0;
            if (sortColumn === 'title') {
                cmp = (a.title ?? '').localeCompare(b.title ?? '', undefined, { sensitivity: 'base' });
            } else if (sortColumn === 'message') {
                cmp = (a.message ?? '').localeCompare(b.message ?? '', undefined, { sensitivity: 'base' });
            } else if (sortColumn === 'startDate') {
                cmp = dateToSortValue(a.startDate) - dateToSortValue(b.startDate);
            } else if (sortColumn === 'endDate') {
                cmp = dateToSortValue(a.endDate) - dateToSortValue(b.endDate);
            } else if (sortColumn === 'createdBy') {
                cmp = (a.createdBy ?? '').localeCompare(b.createdBy ?? '', undefined, { sensitivity: 'base' });
            }
            return cmp * dir;
        });
    }, [filteredRows, sortColumn, sortDirection]);

    const paginatedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const totalCount = sortedRows.length;
    const totalPages = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    const from = totalCount === 0 ? 0 : page * rowsPerPage + 1;
    const to = Math.min(page * rowsPerPage + rowsPerPage, totalCount);

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            setSelectedIds(paginatedRows.map((row) => row.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleClick = (event, id) => {
        const idx = selectedIds.indexOf(id);
        if (idx === -1) setSelectedIds(selectedIds.concat(id));
        else setSelectedIds(selectedIds.filter((s) => s !== id));
    };

    const handleAdd = () => {
        setFormValues(emptyForm);
        setAddError('');
        setAddValidationAttempted(false);
        setAddModalOpen(true);
    };

    const handleAddSubmit = () => {
        if (!isNewsFormValid) {
            setAddValidationAttempted(true);
            return;
        }
        setAddError('');
        setAddValidationAttempted(false);
        const url = ApiList.addnewsmessage;
        axios.post(url, formValues, {
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        }).then(() => {
            setAddModalOpen(false);
            fetchNewsMessages();
        }).catch((err) => {
            setAddError(err.response?.data?.message || err.message || 'Failed to add message.');
        });
    };

    const handleEdit = (row) => {
        setEditRow(row);
        setFormValues({
            title: row.title ?? '',
            message: row.message ?? '',
            startDate: row.startDate ?? '',
            endDate: row.endDate ?? '',
            createdBy: row.createdBy ?? '',
        });
        setEditError('');
        setEditValidationAttempted(false);
        setEditModalOpen(true);
    };

    const handleEditSubmit = () => {
        if (!editRow) return;
        if (!isNewsFormValid) {
            setEditValidationAttempted(true);
            return;
        }
        setEditError('');
        setEditValidationAttempted(false);
        const url = ApiList.updatenewsmessage;
        const payload = {
            old: {
                title: editRow.title,
                message: editRow.message,
                startDate: editRow.startDate,
                endDate: editRow.endDate,
                createdBy: editRow.createdBy,
            },
            updated: formValues,
        };
        axios.post(url, payload, {
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        }).then(() => {
            setEditModalOpen(false);
            setEditRow(null);
            fetchNewsMessages();
        }).catch((err) => {
            setEditError(err.response?.data?.message || err.message || 'Failed to update message.');
        });
    };

    const handleDeleteClick = () => {
        setDeleteError('');
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = () => {
        const rowsToDelete = newsRows.filter((r) => selectedIds.includes(r.id));
        if (rowsToDelete.length === 0) {
            setSelectedIds([]);
            setDeleteModalOpen(false);
            return;
        }
        const payload = rowsToDelete.map((r) => ({
            title: r.title,
            message: r.message,
            startDate: r.startDate,
            endDate: r.endDate,
            createdBy: r.createdBy,
        }));
        setDeleteError('');
        const url = ApiList.deletenewsmessages;
        axios.delete(url, {
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            data: payload,
        }).then(() => {
            setSelectedIds([]);
            setDeleteModalOpen(false);
            setPage(0);
            fetchNewsMessages();
        }).catch((err) => {
            setDeleteError(err.response?.data?.message || err.message || 'Failed to delete.');
        });
    };

    const handleDeleteCancel = () => {
        setDeleteError('');
        setDeleteModalOpen(false);
    };

    const escapeCsv = (value) => {
        if (value == null) return '';
        const s = String(value);
        if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
        return s;
    };

    const handleExport = () => {
        const today = new Date();
        const dateStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
        const headers = ['Title', 'Message', 'Start Date', 'End Date', 'Created By'];
        const headerRow = headers.join(',');
        const dataRows = newsRows.map((r) =>
            [r.title, r.message, r.startDate, r.endDate, r.createdBy].map(escapeCsv).join(',')
        );
        const csv = [headerRow, ...dataRows].join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `News Message Table ${dateStr}.csv`);
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleSearchApply = () => {
        setPage(0);
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(column);
            setSortDirection(column === 'endDate' || column === 'startDate' ? 'desc' : 'asc');
        }
        setPage(0);
    };

    const isSelected = (id) => selectedIds.indexOf(id) !== -1;
    const selectedOnPageCount = paginatedRows.filter((row) => selectedIds.includes(row.id)).length;

    const allFieldsFilled = [formValues.title, formValues.message, formValues.startDate, formValues.endDate, formValues.createdBy].every(
        (v) => (v ?? '').trim() !== ''
    );
    const datesValid = isValidMmDdYyyy(formValues.startDate) && isValidMmDdYyyy(formValues.endDate);
    const startDateObj = parseMmDdYyyyToDate(formValues.startDate);
    const endDateObj = parseMmDdYyyyToDate(formValues.endDate);
    const startBeforeEnd = !startDateObj || !endDateObj || startDateObj.getTime() <= endDateObj.getTime();
    const rowsToCheckForOverlap = editRow ? newsRows.filter((row) => row.id !== editRow.id) : newsRows;
    const hasOverlapWithExisting = datesValid && startBeforeEnd && rowsToCheckForOverlap.some((row) =>
        dateRangesOverlap(formValues.startDate, formValues.endDate, row.startDate, row.endDate)
    );
    const isNewsFormValid = allFieldsFilled && datesValid && startBeforeEnd && !hasOverlapWithExisting;
    const dateFieldsHaveContent = ((formValues.startDate ?? '').trim() !== '' || (formValues.endDate ?? '').trim() !== '');
    const saveDisabledDueToDates = dateFieldsHaveContent && (!datesValid || !startBeforeEnd) && !hasOverlapWithExisting;

    return (
        <Box>
            <Grid container sx={styles.masterMargin}>
                <Typography
                    id="newsTableTitle"
                    variant="h2"
                    sx={{
                        ...styles.h2PageTitle,
                        ...newsTableFont,
                        fontSize: '28px',
                        fontWeight: 700,
                        color: '#000',
                        marginBottom: 0.5,
                    }}
                >
                    <FormattedMessage id="maintenanceTable.newsTitle" defaultMessage="News Message Table" />
                </Typography>
                <Grid container sx={{ ...styles.tablePadding, paddingTop: '0.5%' }}>
                    <Paper
                        elevation={0}
                        sx={{
                            width: '100%',
                            ...styles.maintenanceTable,
                            boxShadow: 'none',
                            paddingLeft: 0,
                            paddingRight: 0,
                        }}
                    >
                        <Toolbar
                            sx={{
                                pl: 0,
                                pr: { xs: 1, sm: 1 },
                                pt: 1,
                                pb: 1,
                                ...(selectedIds.length > 0 && {
                                    bgcolor: (theme) =>
                                        alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                                }),
                            }}
                        >
                            <Stack direction="row" sx={{ marginLeft: -2 }}>
                                <IconButton id="newsAddButton" aria-label="Add" onClick={handleAdd}>
                                    <AddIcon />
                                </IconButton>
                                {selectedIds.length > 0 ? (
                                    <Tooltip title="Delete">
                                        <IconButton id="newsDeleteButton" aria-label="Delete" onClick={handleDeleteClick}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Delete">
                                        <span>
                                            <IconButton id="newsDeleteButtonDisabled" aria-label="Delete" disabled>
                                                <DeleteIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                                <IconButton id="newsExportButton" aria-label="Export" onClick={handleExport}>
                                    <DownloadIcon />
                                </IconButton>
                            </Stack>
                            {selectedIds.length > 0 ? (
                                <Typography
                                    sx={{ flex: '1 1 100%', paddingLeft: 2 }}
                                    color="inherit"
                                    variant="subtitle1"
                                    component="div"
                                >
                                    {selectedIds.length} selected
                                </Typography>
                            ) : (
                                <Typography sx={{ flex: '1 1 100%' }} variant="h6" component="div" />
                            )}
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <TextField
                                    size="small"
                                    placeholder="Search..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearchApply()}
                                    sx={{ width: 140, '& .MuiInputBase-input': { py: 0.5 } }}
                                />
                                <IconButton id="newsSearchButton" aria-label="Search" onClick={handleSearchApply} size="small">
                                    <SearchIcon />
                                </IconButton>
                            </Stack>
                        </Toolbar>
                        <TableContainer>
                            <Table id="newsMessageTable" size="small" aria-labelledby="newsTableTitle">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" sx={{ ...styles.TableCellHeader, ...newsTableHeaderCell }}>
                                            <Checkbox
                                                indeterminate={selectedOnPageCount > 0 && selectedOnPageCount < paginatedRows.length}
                                                checked={paginatedRows.length > 0 && selectedOnPageCount === paginatedRows.length}
                                                onChange={handleSelectAllClick}
                                                inputProps={{ 'aria-label': 'select all' }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ ...styles.TableCellHeader, ...newsTableHeaderCell }} sortDirection={sortColumn === 'title' ? sortDirection : false}>
                                            <TableSortLabel active={sortColumn === 'title'} direction={sortColumn === 'title' ? sortDirection : 'asc'} onClick={() => handleSort('title')}>
                                                <FormattedMessage id="maintenanceTable.newsTitleColumn" defaultMessage="Title of Message" />
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ ...styles.TableCellHeader, ...newsTableHeaderCell }} sortDirection={sortColumn === 'message' ? sortDirection : false}>
                                            <TableSortLabel active={sortColumn === 'message'} direction={sortColumn === 'message' ? sortDirection : 'asc'} onClick={() => handleSort('message')}>
                                                <FormattedMessage id="maintenanceTable.newsMessage" defaultMessage="Message" />
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ ...styles.TableCellHeader, ...newsTableHeaderCell }} sortDirection={sortColumn === 'startDate' ? sortDirection : false}>
                                            <TableSortLabel active={sortColumn === 'startDate'} direction={sortColumn === 'startDate' ? sortDirection : 'asc'} onClick={() => handleSort('startDate')}>
                                                <FormattedMessage id="maintenanceTable.newsStartDate" defaultMessage="Start Date (MM/DD/YYYY)" />
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ ...styles.TableCellHeader, ...newsTableHeaderCell }} sortDirection={sortColumn === 'endDate' ? sortDirection : false}>
                                            <TableSortLabel active={sortColumn === 'endDate'} direction={sortColumn === 'endDate' ? sortDirection : 'asc'} onClick={() => handleSort('endDate')}>
                                                <FormattedMessage id="maintenanceTable.newsEndDate" defaultMessage="End Date (MM/DD/YYYY)" />
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ ...styles.TableCellHeader, ...newsTableHeaderCell }} sortDirection={sortColumn === 'createdBy' ? sortDirection : false}>
                                            <TableSortLabel active={sortColumn === 'createdBy'} direction={sortColumn === 'createdBy' ? sortDirection : 'asc'} onClick={() => handleSort('createdBy')}>
                                                <FormattedMessage id="maintenanceTable.newsCreatedBy" defaultMessage="Created By" />
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ ...styles.TableCellHeader, ...newsTableHeaderCell }} align="center">
                                            {/* Actions column - no header label */}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedRows.length > 0 ? (
                                        paginatedRows.map((row) => {
                                            const rowSelected = isSelected(row.id);
                                            return (
                                                <StyledTableRow
                                                    key={row.id}
                                                    hover
                                                    onClick={(event) => handleClick(event, row.id)}
                                                    selected={rowSelected}
                                                    tabIndex={-1}
                                                >
                                                    <TableCell padding="checkbox" sx={{ ...styles.OrderDetailOptionTableCell, ...newsTableBodyCell }} onClick={(e) => e.stopPropagation()}>
                                                        <Checkbox
                                                            checked={rowSelected}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                handleClick(e, row.id);
                                                            }}
                                                            inputProps={{ 'aria-labelledby': `news-row-${row.id}` }}
                                                        />
                                                    </TableCell>
                                                    <StyledTableCell sx={{ ...styles.OrderDetailOptionTableCell, ...newsTableBodyCell }} id={`news-row-${row.id}`}>
                                                        {row.title}
                                                    </StyledTableCell>
                                                    <StyledTableCell sx={{ ...styles.OrderDetailOptionTableCell, ...newsTableBodyCell }}>{row.message}</StyledTableCell>
                                                    <StyledTableCell sx={{ ...styles.OrderDetailOptionTableCell, ...newsTableBodyCell }}>{row.startDate}</StyledTableCell>
                                                    <StyledTableCell sx={{ ...styles.OrderDetailOptionTableCell, ...newsTableBodyCell }}>{row.endDate}</StyledTableCell>
                                                    <StyledTableCell sx={{ ...styles.OrderDetailOptionTableCell, ...newsTableBodyCell }}>{row.createdBy}</StyledTableCell>
                                                    <StyledTableCell sx={{ ...styles.OrderDetailOptionTableCell, ...newsTableBodyCell }} align="center" padding="none">
                                                        <IconButton
                                                            aria-label="Edit"
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEdit(row);
                                                            }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </StyledTableCell>
                                                </StyledTableRow>
                                            );
                                        })
                                    ) : (
                                        <StyledTableRow>
                                            <TableCell colSpan={7} sx={{ ...styles.OrderDetailOptionTableCell, ...newsTableBodyCell }}>
                                                <FormattedMessage id="maintenanceTable.noNewsToDisplay" defaultMessage="There are no messages available." />
                                            </TableCell>
                                        </StyledTableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box
                            component="div"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1,
                                padding: '16px 12px',
                                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                                ...newsTableFont,
                                fontSize: '14px',
                                color: newsTableGray,
                            }}
                        >
                            <Box sx={{ flex: '1 1 0', display: 'flex', justifyContent: 'flex-start', minWidth: 0 }}>
                                <Typography component="span">
                                    {totalCount === 0
                                        ? 'Displaying 0-0 of 0'
                                        : `Displaying ${from}-${to} of ${totalCount}`}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: '1 1 0', display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: 0 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                                    <FormattedMessage id="maintenanceTable.rowsPerPage" defaultMessage="Items per page:">
                                        {(msg) => (
                                            <Typography component="span">{msg}</Typography>
                                        )}
                                    </FormattedMessage>
                                    <Select
                                        value={rowsPerPage}
                                        onChange={(e) => {
                                            setRowsPerPage(Number(e.target.value));
                                            setPage(0);
                                        }}
                                        MenuProps={{ disableScrollLock: true }}
                                        variant="standard"
                                        sx={{
                                            ...newsTableFont,
                                            fontSize: '14px',
                                            width: 56,
                                            minWidth: 56,
                                            '& .MuiSelect-select': { paddingRight: '24px', paddingLeft: '8px' },
                                        }}
                                    >
                                        <MenuItem value={5}>5</MenuItem>
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={25}>25</MenuItem>
                                    </Select>
                                </Box>
                            </Box>
                            <Box sx={{ flex: '1 1 0', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minWidth: 0 }}>
                                <IconButton
                                    onClick={() => setPage(0)}
                                    disabled={page === 0}
                                    aria-label="first page"
                                    size="small"
                                >
                                    <FirstPageIcon />
                                </IconButton>
                                <IconButton
                                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                                    disabled={page === 0}
                                    aria-label="previous page"
                                    size="small"
                                >
                                    <KeyboardArrowLeftIcon />
                                </IconButton>
                                <Typography component="span" sx={{ px: 1, minWidth: 24, textAlign: 'center' }}>
                                    {page + 1}
                                </Typography>
                                <IconButton
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page >= totalPages || totalCount === 0}
                                    aria-label="next page"
                                    size="small"
                                >
                                    <KeyboardArrowRightIcon />
                                </IconButton>
                                <IconButton
                                    onClick={() => setPage(totalPages)}
                                    disabled={page >= totalPages || totalCount === 0}
                                    aria-label="last page"
                                    size="small"
                                >
                                    <LastPageIcon />
                                </IconButton>
                            </Box>
                        </Box>
                        <Modal open={deleteModalOpen} onClose={handleDeleteCancel} aria-labelledby="news-delete-modal-title" aria-describedby="news-delete-modal-description">
                            <Box sx={styles.modalSx}>
                                <Typography id="news-delete-modal-title" variant="h6" component="h2">
                                    <WarningIcon fontSize="small" />{' '}
                                    <FormattedMessage id="maintenanceTable.deleteMessage" defaultMessage="The following item(s) will be deleted: " />
                                    <strong>{selectedIds.length} selected</strong>
                                </Typography>
                                {deleteError && (
                                    <Typography color="error" sx={{ pt: 2 }}>{deleteError}</Typography>
                                )}
                                <Stack direction="row" spacing={2} sx={{ paddingTop: 5 }}>
                                    <Button sx={styles.deleteCancel} onClick={handleDeleteCancel}>
                                        <FormattedMessage id="maintenanceTable.cancel" defaultMessage="No, Cancel" />
                                    </Button>
                                    <Button sx={styles.deleteConfirm} onClick={handleDeleteConfirm}>
                                        <FormattedMessage id="maintenanceTable.confirm" defaultMessage="Yes, Confirm" />
                                    </Button>
                                </Stack>
                            </Box>
                        </Modal>
                        <Modal open={addModalOpen} onClose={() => { setAddModalOpen(false); setAddValidationAttempted(false); }} aria-labelledby="news-add-modal-title">
                            <Box sx={styles.modalSx}>
                                <Typography id="news-add-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                                    <FormattedMessage id="maintenanceTable.addNews" defaultMessage="Add News Message" />
                                </Typography>
                                {addError && (
                                    <Typography color="error" sx={{ mb: 1 }}>{addError}</Typography>
                                )}
                                <Stack spacing={1.5}>
                                    <TextField label="Title" value={formValues.title} onChange={(e) => setFormValues((v) => ({ ...v, title: e.target.value }))} fullWidth size="small" />
                                    <TextField label="Message" value={formValues.message} onChange={(e) => setFormValues((v) => ({ ...v, message: e.target.value }))} fullWidth size="small" multiline rows={3} />
                                    <TextField label="Start Date (MM/DD/YYYY)" value={formValues.startDate} onChange={(e) => setFormValues((v) => ({ ...v, startDate: formatDateMmDdYyyy(e.target.value) }))} fullWidth size="small" placeholder="__/__/____" inputProps={{ maxLength: 10 }} />
                                    <TextField label="End Date (MM/DD/YYYY)" value={formValues.endDate} onChange={(e) => setFormValues((v) => ({ ...v, endDate: formatDateMmDdYyyy(e.target.value) }))} fullWidth size="small" placeholder="__/__/____" inputProps={{ maxLength: 10 }} />
                                    <TextField label="Created By" value={formValues.createdBy} onChange={(e) => setFormValues((v) => ({ ...v, createdBy: e.target.value }))} fullWidth size="small" />
                                </Stack>
                                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                                    <Button sx={styles.deleteCancel} onClick={() => { setAddModalOpen(false); setAddValidationAttempted(false); }}>Cancel</Button>
                                    <Button sx={styles.deleteConfirm} onClick={handleAddSubmit} disabled={!isNewsFormValid}>Save</Button>
                                </Stack>
                                {hasOverlapWithExisting && (
                                    <Typography color="error" sx={{ mt: 1 }}>A News Message is already scheduled within those dates.</Typography>
                                )}
                                {saveDisabledDueToDates && !addValidationAttempted && (
                                    <Typography color="error" sx={{ mt: 1 }}>
                                        {datesValid && !startBeforeEnd ? 'The End Date cannot be before the Start Date.' : 'The Start or End Date is not valid.'}
                                    </Typography>
                                )}
                                {addValidationAttempted && !isNewsFormValid && !hasOverlapWithExisting && (
                                    <Typography color="error" sx={{ mt: 1 }}>
                                        {!allFieldsFilled ? 'Please fill in all fields.' : !datesValid ? 'Please use valid dates (MM/DD/YYYY). Month 01–12, day 01–31, year before 2999.' : 'The End Date cannot be before the Start Date.'}
                                    </Typography>
                                )}
                            </Box>
                        </Modal>
                        <Modal open={editModalOpen} onClose={() => { setEditModalOpen(false); setEditRow(null); setEditError(''); setEditValidationAttempted(false); }} aria-labelledby="news-edit-modal-title">
                            <Box sx={styles.modalSx}>
                                <Typography id="news-edit-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                                    <FormattedMessage id="maintenanceTable.editNews" defaultMessage="Edit News Message" />
                                </Typography>
                                {editError && (
                                    <Typography color="error" sx={{ mb: 1 }}>{editError}</Typography>
                                )}
                                <Stack spacing={1.5}>
                                    <TextField label="Title" value={formValues.title} onChange={(e) => setFormValues((v) => ({ ...v, title: e.target.value }))} fullWidth size="small" />
                                    <TextField label="Message" value={formValues.message} onChange={(e) => setFormValues((v) => ({ ...v, message: e.target.value }))} fullWidth size="small" multiline rows={3} />
                                    <TextField label="Start Date (MM/DD/YYYY)" value={formValues.startDate} onChange={(e) => setFormValues((v) => ({ ...v, startDate: formatDateMmDdYyyy(e.target.value) }))} fullWidth size="small" placeholder="__/__/____" inputProps={{ maxLength: 10 }} />
                                    <TextField label="End Date (MM/DD/YYYY)" value={formValues.endDate} onChange={(e) => setFormValues((v) => ({ ...v, endDate: formatDateMmDdYyyy(e.target.value) }))} fullWidth size="small" placeholder="__/__/____" inputProps={{ maxLength: 10 }} />
                                    <TextField label="Created By" value={formValues.createdBy} onChange={(e) => setFormValues((v) => ({ ...v, createdBy: e.target.value }))} fullWidth size="small" />
                                </Stack>
                                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                                    <Button sx={styles.deleteCancel} onClick={() => { setEditModalOpen(false); setEditRow(null); setEditError(''); setEditValidationAttempted(false); }}>Cancel</Button>
                                    <Button sx={styles.deleteConfirm} onClick={handleEditSubmit}>Save</Button>
                                </Stack>
                                {hasOverlapWithExisting && (
                                    <Typography color="error" sx={{ mt: 1 }}>A News Message is already scheduled within those dates.</Typography>
                                )}
                                {saveDisabledDueToDates && !editValidationAttempted && (
                                    <Typography color="error" sx={{ mt: 1 }}>
                                        {datesValid && !startBeforeEnd ? 'The End Date cannot be before the Start Date.' : 'The Start or End Date is not valid.'}
                                    </Typography>
                                )}
                                {editValidationAttempted && !isNewsFormValid && !hasOverlapWithExisting && (
                                    <Typography color="error" sx={{ mt: 1 }}>
                                        {!allFieldsFilled ? 'Please fill in all fields.' : !datesValid ? 'Please use valid dates (MM/DD/YYYY). Month 01–12, day 01–31, year before 2999.' : 'The End Date cannot be before the Start Date.'}
                                    </Typography>
                                )}
                            </Box>
                        </Modal>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
